import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableMenu, TableColumn, MenuOption } from "../../components/Table";
import { DashboardHeader } from "../../components/DashboardHeader";
import { initAllStores } from "../../utils/cache";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { sendRequest, } from "../../utils/api";
import { GuestInfoModal } from "../../Modal/bookings/GuestDetailMoadal";
import Customer from "../../models/Customer";
import { PAGINATION_PAGE_NUMBER } from "../../constants";

import GuestBillPopup from "../../Modal/accounting/GuestBillList";
import Bill from "../../models/Bill";
import { checkAccess, computeGuestActionAccess } from "../../utils/access";
import { syncBillHistory } from "../../utils/sync";


const columns: TableColumn[] = [
  { key: "name", label: "Name" },
  // {
  //   key: "paymentStatus",
  //   label: "Payment Status",
  // getClassName: (value) =>
  //   value === "Paid"
  //     ? "text-green-600 font-semibold"
  //     : "text-red-500 font-semibold",
  //   responsive: "md"
  // },
  { key: "gender", label: "Gender", responsive: "md" },
  { key: "phone", label: "Phone" },
  { key: "state", label: "State", responsive: "md" },
  { key: "nationality", label: "Nationality", responsive: "md" },
];

const title = "Guests";
const categories = ["Pending", "Paid"];

const GuestPage = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const [search, setSearch] = useState("");
  const [guests, setGuests] = useState<Customer[]>([] as Customer[]);
  const [loading, setLoading] = useState(false);
  // const [showModal, setShowModal] = useState(false);

  const [showBillList, setShowBillList] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<Customer>({} as Customer);
  // const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const [billList, setBillList] = useState<Bill[]>([] as Bill[]);

  const [access, setAccess] = useState({ deleteGuest: false, updateGuest: false });

  useEffect(() => {
    const { deleteGuest } = computeGuestActionAccess("deleteGuest");
    const { updateGuest } = computeGuestActionAccess("updateGuest");

    setAccess({ deleteGuest, updateGuest });
  }, [])

  const syncGuest = async () => {
    setLoading(true);
    try {
      const cache = await initAllStores();

      const guests: Customer[] = await sendRequest("GET", "/guests");
      // console.log(guests)
      await cache.clear("guests");
      await cache.saveMany({ guests });
      setGuests(guests);

      // Update last sync timestamp
      const now = new Date().toISOString();
      await cache.updateManyBy("data", ["lastSyncTime"], {
        guests: now,
      });

      setLastSyncedAt(new Date());
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
      syncBillHistory({
        orders: [],
        bookedRooms: [],
      });
    }
  }

  useEffect(() => {
    const getGuest = async () => {
      setLoading(true);
      try {
        const cache = await initAllStores();
        const guests: Customer[] = await cache.getAllBy("guests", {}, "name");
        const lastSyncTime = await cache.getBy("data", { id: "lastSyncTime" });
        setGuests(guests);
        setLastSyncedAt(
          lastSyncTime.guests ? new Date(lastSyncTime.guests) : null
        );
      } catch (err: any) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
    getGuest();
  }, []);

  const deleteGuest = async (guestId: string) => {
    setLoading(true);
    try {
      const cache = await initAllStores();
      await sendRequest("DELETE", `/guests/${guestId}/delete`);
      setGuests((prevBookings) =>
        prevBookings
          .filter((g: Customer) => g.id !== guestId)
      )
      await cache.deleteOne("guests", guestId);
      showToast("Guest has been successfully deleted.", { type: "success" });
    } catch (error: any) {
      showToast(
        "An error occurred while deleting guest. "
        + "Please try again later.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const guestDetail = async (guestId: string) => {
    try {
      const cache = await initAllStores();
      setLoading(true);

      const guests = await cache.getBy("guests", { id: guestId });
      // console.log(guests)
      setSelectedGuest(guests);
      setShowGuestModal(true);
    } catch (err: any) {
      console.log(err)
      showToast(
        "Unable to load the guest information. "
        + "Please check your connection and try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }

  const updateGuestData = async (data: Customer) => {

    const isAccess = checkAccess(
      "updateGuest", access, openModal, closeModal
    );
    if (!isAccess) return;

    const { bills, ...guest } = data;
    setLoading(true);
    setShowGuestModal(false);
    try {
      const cache = await initAllStores();

      await sendRequest("PUT", "/guests", guest);

      const booking = await cache.getBy("bookings", { customerId: guest.id });
      await cache.updateManyBy("bookings", [booking.id], { customer: data, customerId: guest.id });
      showToast("Guest details updated successfully!", { type: "success" });

      setGuests((prevBookings) =>
        prevBookings.map((g) =>
          g.id === guest.id
            ? {
              ...g,
              ...data,
            }
            : g
        )
      );
    } catch (err: any) {
      console.log(err)
      showToast(
        "Unable to update the guest information. Please check your connection and try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }


  const searchGuest = async () => {
    if (!search.trim()) return;

    setLoading(true);
    try {
      // 🔹 Call your real API
      const response = await sendRequest("GET", `/guests/search?query=${search}`);

      // ✅ Expecting the backend to return { data: [...] }
      setGuests(response || []);
    } catch (err) {
      console.error(err);
      openModal("Search Failed", "Could not fetch guest data. Try again.", closeModal, false, "Close");
    } finally {
      setLoading(false);
    }
  };

  // const bookingsByDate = async (start: string, end: string) => {
  //   syncGuest({ start, end });
  // }

  const filteredData = guests.filter((b) => {
    const matchesSearch = (
      b?.name?.toLowerCase().includes(search.toLowerCase())
    );
    // if (selectedCategory) {
    //   const matchesCategory =
    //     (selectedCategory === "Pending" && b.paymentStatus === "Pending") ||
    //     (selectedCategory === "Paid" && b.paymentStatus === "Paid");
    //   return matchesSearch && matchesCategory;
    // }
    return matchesSearch;
  });

  return (
    <div className="p-4">
      <DashboardHeader
        title={title}
        categories={categories}
        search={search}
        onClickSearch={searchGuest}
        setSearch={setSearch}
        showFilter={false}
        onSync={() => {
          setSearch("");
          syncGuest();
        }}
        lastSyncedAt={lastSyncedAt}
      />

      <FullPageSpinner show={loading} />

      <GuestBillPopup
        open={showBillList}
        guest={selectedGuest}
        onClose={() => setShowBillList(false)}
        bills={billList}
      />

      {showGuestModal && (
        <GuestInfoModal
          guest={selectedGuest}
          onClose={() => setShowGuestModal(false)}
          onSave={(data: Customer) => updateGuestData(data)}
        />
      )}


      {filteredData.length >= 0 && (
        <Table
          columns={columns}
          data={filteredData}
          rowsPerPage={PAGINATION_PAGE_NUMBER}
          tableMenu={(row: Customer, closeMenu) => (
            <TableMenu
              row={row}
              options={[
                {
                  label: "Details",
                  onClick: (c: Customer) => guestDetail(c.id),
                },
                {
                  label: "Guest Bill",
                  onClick: (c: Customer) => {
                    setBillList(c?.bills ?? []);
                    setSelectedGuest(c)
                    setShowBillList(true);
                  },
                },
                {
                  label: "Allocate Room",
                  onClick: (c: Customer) => { navigate(`/rooms/book?customerId=${c.id}`, { state: c }) },
                },
                {
                  label: "Delete",
                  color: "text-red-500",
                  onClick: (c: Customer) => {
                    const isAccess = checkAccess(
                      "deleteGuest", access, openModal, closeModal
                    );
                    if (!isAccess) return;
                    return openModal(
                      "Delete Guest ⚠️",
                      c.name
                        ? `Are you sure you want to delete ${c.name}'s record? This action cannot be undone.`
                        : "Are you sure you want to delete this guest? This action cannot be undone.",
                      () => deleteGuest(c.id), true, "Yes, Delete", "Cancel", "red"
                    );
                  },
                },
              ].filter(Boolean) as MenuOption[]}
              closeMenu={closeMenu}
            />
          )}
        />
      )}
    </div>
  );
};
export default GuestPage;